import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllDiagnosisReports, deleteReport, getDiagnosisReportById } from '../../services/diagnosisReports.service';
import { calculateFinancialDiagnosis, calculateBusinessDiagnosis } from '../../utils/diagnosisEngine';
import { calculateBusinessTax } from '../../utils/taxCalculations';
import { generateProfessionalPDF } from '../../utils/pdfGenerator';
import '../../styles/admin-diagnosis.css';

const DiagnosisReports = () => {
    const [reports, setReports] = useState([]);
    const [filteredReports, setFilteredReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [incomeTypeFilter, setIncomeTypeFilter] = useState('all');
    const [businessCategoryFilter, setBusinessCategoryFilter] = useState('all');
    const [downloadingId, setDownloadingId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        fetchReports();
    }, []);

    useEffect(() => {
        filterAndSortReports();
        setCurrentPage(1); // Reset to first page on filter change
    }, [reports, searchTerm, statusFilter, incomeTypeFilter, businessCategoryFilter]);

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredReports.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const fetchReports = async () => {
        try {
            setLoading(true);
            const data = await getAllDiagnosisReports();
            setReports(data);
        } catch (error) {
            console.error('Error fetching reports:', error);
            alert('Error loading reports. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortReports = () => {
        let filtered = [...reports];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(report =>
                report.personalInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                report.personalInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                report.personalInfo?.phone?.includes(searchTerm) ||
                report.reportId?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(report => report.status === statusFilter);
        }

        // Income Type filter
        if (incomeTypeFilter !== 'all') {
            filtered = filtered.filter(report => report.personalInfo?.incomeType === incomeTypeFilter);

            // Business Category filter (only if business proprietor is selected)
            if (incomeTypeFilter === 'business' && businessCategoryFilter !== 'all') {
                filtered = filtered.filter(report => report.personalInfo?.businessCategory === businessCategoryFilter);
            }
        }

        // Sort by date (Default)
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setFilteredReports(filtered);
    };

    const handleDelete = async (reportId) => {
        if (window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
            try {
                await deleteReport(reportId);
                alert('Report deleted successfully');
                fetchReports();
            } catch (error) {
                console.error('Error deleting report:', error);
                alert('Error deleting report. Please try again.');
            }
        }
    };

    const getSavingsRate = (report) => {
        try {
            // Check if pre-calculated value exists
            if (report.financialHealth?.savingsRate !== undefined && report.financialHealth?.savingsRate !== null) {
                const parsed = parseFloat(report.financialHealth.savingsRate);
                if (!isNaN(parsed)) {
                    return Math.min(Math.max(0, parsed), 100);
                }
            }

            // Fallback: Dynamic calculation if possible
            const results = report.personalInfo?.incomeType === 'salary'
                ? calculateFinancialDiagnosis(report)
                : calculateBusinessDiagnosis(report);

            const rate = results?.metrics?.savingsRate || results?.metrics?.netMargin || 0;
            const parsedRate = parseFloat(rate);

            if (isNaN(parsedRate)) return 0;
            return Math.min(Math.max(0, parsedRate), 100);
        } catch (e) {
            return 0;
        }
    };

    const handleDownload = async (reportId) => {
        try {
            setDownloadingId(reportId);
            const report = await getDiagnosisReportById(reportId);
            if (!report) throw new Error("Report not found");

            // --- REUSE CALCULATION LOGIC FROM DETAIL PAGE ---
            const incomeType = report.personalInfo?.incomeType || 'salary';
            let calculations = null;

            if (incomeType === 'salary') {
                const diagnosisResults = calculateFinancialDiagnosis(report);
                const baseSalary = parseFloat(report.personalInfo?.monthlySalary) || 0;
                const otherIncome = parseFloat(report.personalInfo?.otherIncomeAmount) || 0;
                const monthlySalary = baseSalary + otherIncome;
                const annualSal = monthlySalary * 12;

                const standardDeductionOld = 50000;
                const standardDeductionNew = 75000;

                const epfAnnual = (parseFloat(report.salaryIncome?.epf) || 0) * 12;
                const ppfAnnual = (parseFloat(report.salaryIncome?.ppf) || 0) * 12;
                const elssAnnual = (parseFloat(report.salaryInvestment?.elss) || 0) * 12;
                const homeLoanPrincipal = (parseFloat(report.salaryIncome?.homeLoanEMI) || 0) * 12 * 0.40;
                const insurance80C = (parseFloat(report.salaryIncome?.lifeInsurance) || 0) * 12;

                const raw80C = epfAnnual + ppfAnnual + elssAnnual + homeLoanPrincipal + insurance80C;
                const total80C = Math.min(150000, raw80C);
                const hraExemption = parseFloat(report.taxDetails?.hraExemption) || 0;
                const homeLoanInterest = Math.min(200000, (parseFloat(report.salaryIncome?.homeLoanEMI) || 0) * 12 * 0.60);
                const insurance80D = (parseFloat(report.salaryIncome?.healthInsurance) || 0) * 12;

                const totalDeductionsOld = standardDeductionOld + total80C + insurance80D + hraExemption + homeLoanInterest;
                const taxableIncomeOld = Math.max(0, annualSal - totalDeductionsOld);
                const taxableIncomeNew = Math.max(0, annualSal - standardDeductionNew);

                // Simple Tax Calculation for PDF (Mirroring DETAIL page logic exactly)
                const calculateTaxOld = (ti) => {
                    let t = 0; if (ti <= 250000) return 0;
                    if (ti > 250000) t += Math.min(ti - 250000, 250000) * 0.05;
                    if (ti > 500000) t += Math.min(ti - 500000, 500000) * 0.20;
                    if (ti > 1000000) t += (ti - 1000000) * 0.30;
                    return t;
                };

                const calculateTaxNew = (ti) => {
                    let t = 0; if (ti <= 300000) return 0;
                    if (ti > 300000) t += Math.min(ti - 300000, 400000) * 0.05;
                    if (ti > 700000) t += Math.min(ti - 700000, 300000) * 0.10;
                    if (ti > 1000000) t += Math.min(ti - 1000000, 200000) * 0.15;
                    if (ti > 1200000) t += Math.min(ti - 1200000, 300000) * 0.20;
                    if (ti > 1500000) t += (ti - 1500000) * 0.30;
                    return ti <= 700000 ? 0 : t;
                };

                const oldTax = calculateTaxOld(taxableIncomeOld);
                const newTax = calculateTaxNew(taxableIncomeNew);

                calculations = {
                    ...diagnosisResults,
                    incomeType: 'salary',
                    monthlySalary,
                    bestRegime: oldTax <= newTax ? 'Old Regime' : 'New Regime',
                    taxSavings: Math.abs(oldTax - newTax),
                    oldRegimeTax: oldTax,
                    newRegimeTax: newTax,
                    healthScore: diagnosisResults.grades.overall,
                    standardDeductionOld,
                    standardDeductionNew,
                    total80C,
                    insurance80D,
                    totalDeductionsOld,
                    taxableIncomeOld,
                    taxableIncomeNew,
                    monthlySavings: diagnosisResults.metrics.totalMonthlyInvestments,
                    totalExpenses: diagnosisResults.metrics.totalEssentialExpenses + diagnosisResults.metrics.totalLifestyleExpenses,
                    savingsPercentage: parseFloat(diagnosisResults.metrics.savingsRate.toFixed(1)),
                    totalTaxPayable: Math.min(oldTax, newTax)
                };
            } else if (incomeType === 'business' || incomeType === 'business_partnership') {
                const bizDiagnosis = calculateBusinessDiagnosis(report);
                const bDetails = report.businessDetails || {};
                const bInv = report.businessInvestment || {};
                const salesTurnover = parseFloat(bDetails.grossTurnover || 0);
                const otherInc = (parseFloat(bInv?.savingInterest || 0)) + (parseFloat(bInv?.fdInterest || 0));

                const taxCalc = calculateBusinessTax({
                    salesTurnover,
                    otherIncome: otherInc,
                    totalExpenses: bizDiagnosis.metrics.totalCOGS + bizDiagnosis.metrics.totalOpEx
                });

                calculations = {
                    ...bizDiagnosis,
                    incomeType: 'business',
                    salesTurnover: taxCalc.salesTurnover,
                    totalExpenses: taxCalc.totalExpenses,
                    netProfitBeforeTax: taxCalc.netProfitBeforeTax,
                    totalTaxPayable: taxCalc.totalTaxPayable,
                    monthlyIncome: bizDiagnosis.metrics.monthlyRevenue,
                    monthlySavings: bizDiagnosis.metrics.monthlyProfit,
                    savingsPercentage: bizDiagnosis.metrics.netMargin.toFixed(1),
                    healthScore: bizDiagnosis.grades.overall,
                    bestRegime: 'New Regime (Business)',
                    taxableIncomeNew: taxCalc.netProfitBeforeTax
                };
            }

            // --- EXECUTE 10-PAGE PDF GENERATION ---
            generateProfessionalPDF(report, calculations);

        } catch (error) {
            console.error("PDF Generation Error", error);
            alert("Failed to generate professional report. Please check data.");
        } finally {
            setDownloadingId(null);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="admin-content-v2" style={{ display: 'flex', minHeight: '80vh' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', flex: 1 }}>
                    <div className="custom-loader" style={{ width: '55px', height: '80px' }}>
                        <svg viewBox="0 0 55 80" xmlns="http://www.w3.org/2000/svg" fill="#0EAB5EFF">
                            <g transform="matrix(1 0 0 -1 0 80)">
                                <rect width="10" height="20" rx="3">
                                    <animate attributeName="height" begin="0s" dur="4.3s" values="20;45;57;80;64;32;66;45;64;23;66;13;64;56;34;34;2;23;76;79;20" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="15" width="10" height="80" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="80;55;33;5;75;23;73;33;12;14;60;80" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="30" width="10" height="50" rx="3">
                                    <animate attributeName="height" begin="0s" dur="1.4s" values="50;34;78;23;56;23;34;76;80;54;21;50" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="45" width="10" height="30" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="30;45;13;80;56;72;45;76;34;23;67;30" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                            </g>
                        </svg>
                    </div>
                    <p style={{ marginTop: '20px', color: '#64748B', fontWeight: 500 }}>Loading diagnosis reports...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-content-v2">
            <div className="diagnosis-header-v2">
                <h1 className="nav-title-v2">Financial Diagnosis Reports</h1>
                <p className="nav-subtitle-v2">Review and manage participant financial health data</p>
            </div>

            {/* Stats Summary */}
            <div className="stats-summary-v2">
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Total Reports</span>
                    <span className="stat-value-v2">{reports.length}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Active</span>
                        <span className="stat-sub-v2">database entries</span>
                    </div>
                </div>
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Filtered View</span>
                    <span className="stat-value-v2">{filteredReports.length}</span>
                    <div className="stat-trend-v2" style={{ color: '#3B82F6' }}>
                        <span>Matching</span>
                        <span className="stat-sub-v2">current criteria</span>
                    </div>
                </div>
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Pending Review</span>
                    <span className="stat-value-v2">{reports.filter(r => r.status === 'pending').length}</span>
                    <div className="stat-trend-v2" style={{ color: '#F59E0B' }}>
                        <span>Awaiting</span>
                        <span className="stat-sub-v2">admin action</span>
                    </div>
                </div>
                <div className="stat-card-v2">
                    <span className="stat-label-v2">Completed</span>
                    <span className="stat-value-v2">{reports.filter(r => r.status === 'completed').length}</span>
                    <div className="stat-trend-v2 positive">
                        <span>Done</span>
                        <span className="stat-sub-v2">successfully processed</span>
                    </div>
                </div>
            </div>

            {/* Filters bar */}
            <div className="filters-bar-v2">
                <div className="search-wrapper-v2">
                    <input
                        type="text"
                        className="search-input-v2"
                        placeholder="Search by name, email, report ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="filter-controls-v2" style={{ display: 'flex', gap: '12px' }}>
                    <select className="filter-select-v2" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All Status</option>
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                    </select>

                    <select className="filter-select-v2" value={incomeTypeFilter} onChange={(e) => {
                        setIncomeTypeFilter(e.target.value);
                        setBusinessCategoryFilter('all'); // Reset category when type changes
                    }}>
                        <option value="all">All Income Types</option>
                        <option value="salary">Salary</option>
                        <option value="business">Business (Proprietor)</option>
                        <option value="business_partnership">Business (Firm/LLP/Pvt Ltd)</option>
                    </select>

                    {incomeTypeFilter === 'business' && (
                        <select className="filter-select-v2" value={businessCategoryFilter} onChange={(e) => setBusinessCategoryFilter(e.target.value)}>
                            <option value="all">All Categories</option>
                            <option value="manufacturing">Manufacturing</option>
                            <option value="services">Services</option>
                            <option value="trading">Trading</option>
                        </select>
                    )}
                </div>
            </div>

            {/* Reports Table Card */}
            <div className="table-card-v2">
                <div className="table-card-header-v2">
                    <h3>Submissions List</h3>
                </div>

                <div className="table-responsive">
                    <table className="v2-table">
                        <thead>
                            <tr>
                                <th>Participant</th>
                                <th>Email & Contact</th>
                                <th>Report ID</th>
                                <th>Savings</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((report) => (
                                <tr key={report.id || report.reportId}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar-small">
                                                {report.personalInfo?.name?.charAt(0).toUpperCase() || 'P'}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontWeight: 600, color: 'var(--admin-v2-primary)' }}>{report.personalInfo?.name || 'Unknown'}</span>
                                                <span style={{ fontSize: '11px', color: 'var(--admin-v2-text-muted)' }}>{report.personalInfo?.city || 'N/A'}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '13px' }}>{report.personalInfo?.email || 'N/A'}</span>
                                            <span style={{ fontSize: '11px', color: 'var(--admin-v2-text-muted)' }}>{report.personalInfo?.phone || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{report.reportId}</td>
                                    <td>
                                        <div className="v2-progress-bar">
                                            <div className="v2-progress-fill" style={{ width: `${getSavingsRate(report)}%` }}></div>
                                        </div>
                                    </td>
                                    <td className="actions-cell">
                                        <div className="action-buttons-v2">
                                            <Link
                                                to={`/admin/diagnosis/reports/${report.reportId}`}
                                                className="icon-btn-v2 view"
                                                title="View Details"
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                            </Link>
                                            <button
                                                onClick={() => handleDownload(report.reportId)}
                                                className="icon-btn-v2 download"
                                                title="Download PDF"
                                                disabled={downloadingId === report.reportId}
                                            >
                                                {downloadingId === report.reportId ? (
                                                    <div className="spinner-micro"></div>
                                                ) : (
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(report.reportId)}
                                                className="icon-btn-v2 delete"
                                                title="Delete Report"
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {filteredReports.length > 0 && (
                    <div className="pagination-v2">
                        <div className="pagination-info-v2">
                            Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredReports.length)}</strong> of <strong>{filteredReports.length}</strong> results
                        </div>
                        <div className="pagination-controls-v2">
                            <button
                                className="page-btn-v2"
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                title="Previous Page"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                            </button>

                            <div className="page-numbers-v2" style={{ display: 'flex', gap: '4px' }}>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`page-btn-v2 ${currentPage === i + 1 ? 'active' : ''}`}
                                        onClick={() => paginate(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                            </div>

                            <button
                                className="page-btn-v2"
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                title="Next Page"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DiagnosisReports;
